import styled from '@emotion/styled'
import Box from '../Box'
import { ActivityCardProps } from '../../types/activity.types'
import { ActivityMetadataDesignType } from './ActivityMetadataDesignType'
import { getActivityMetadataDesignType } from '../../config/activity.config'
import { STYLE_COLORS, darkenColor, getContrastTextColor, getActivityDisplayStatus } from './ViewHelper'
import { RegistrationStatusPill } from './RegistrationStatusPill'
import { useCurrency } from '../../hooks/useCurrency'
import { getThemeConfig, resolveActivityHoverBackground } from '../../themeConfig'

export const ActivityCardDesignType: React.FC<ActivityCardProps> = ({ activity, onClick, client, fields }) => {
  const currency = useCurrency(client || undefined)
  const themeConfig = getThemeConfig()
  const styleColor = activity.style
    ? client?.themeConfig?.danceStyles?.find((ds) => ds.name.toLowerCase() === activity.style.toLowerCase())?.color ||
      STYLE_COLORS[activity.style.toLowerCase()] ||
      STYLE_COLORS.default
    : STYLE_COLORS.default

  const textColor = getContrastTextColor(styleColor)
  const displayStatus = getActivityDisplayStatus(activity)

  return (
    <StyledActivityCard
      onClick={onClick}
      fullWidth
      gap="0px"
      $stylecolor={styleColor}
      $textcolor={textColor}
      $activityboxbackground={themeConfig.activityBoxBackground}
      $textcolorconfig={themeConfig.textColor}
      $activityboxborder={themeConfig.activityBoxBorder}
      $activityhover={resolveActivityHoverBackground(themeConfig)}
      key={`activity-${activity.id}`}
      style={{ position: 'relative' }}
    >
      <CardTop $stylecolor={styleColor} $bannerurl={activity.bannerUrl}>
        {activity.bannerUrl && <CardBannerImg src={activity.bannerUrl} alt={activity.name} />}
      </CardTop>
      {displayStatus && (
        <RegistrationStatusPill
          bg={displayStatus.bg}
          color={displayStatus.color}
          icon={displayStatus.icon}
          text={displayStatus.text ?? ''}
          absolute
          top={2}
          left={2}
        />
      )}
      <ActivityCardContent>
        <ActivityTitle>{activity.name}</ActivityTitle>
        <ActivityMetadataDesignType
          activity={activity}
          items={getActivityMetadataDesignType(activity, currency)}
          fields={fields}
        />
      </ActivityCardContent>
    </StyledActivityCard>
  )
}

const CardBannerImg = styled.img`
  width: 100%;
  height: auto;
  max-height: 160px;
  object-fit: cover;
  object-position: left center;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  display: block;
`

const CardTop = styled.div<{ $stylecolor: string; $bannerurl?: string }>`
  width: 100%;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  position: relative;
  overflow: hidden;
  background: ${(props) => (props.$bannerurl ? 'none' : props.$stylecolor)};
  display: flex;
  align-items: stretch;
  justify-content: stretch;
`

const STYLED_ACTIVITY_CARD_TRANSIENT_PROPS = [
  '$stylecolor',
  '$textcolor',
  '$activityboxbackground',
  '$textcolorconfig',
  '$activityboxborder',
  '$activityhover'
]
const StyledActivityCard = styled(Box, {
  shouldForwardProp: (prop) => !STYLED_ACTIVITY_CARD_TRANSIENT_PROPS.includes(prop)
})<{
  $stylecolor: string
  $textcolor: string
  $activityboxbackground: string
  $textcolorconfig: string
  $activityboxborder: string
  $activityhover: string
}>`
  color: ${(props) => props.$textcolorconfig || '#222'} !important;
  border-radius: 8px;
  border: 2px solid ${(props) => props.$activityboxborder || props.$stylecolor};
  cursor: pointer;
  background: ${(props) => props.$activityboxbackground || '#fff'};
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  min-height: 500px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    background: ${(props) => props.$activityhover || props.$activityboxbackground || '#fff'};
  }
`

const ActivityCardContent = styled(Box)`
  padding: 24px 20px 16px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  background: transparent;
`

const ActivityTitle = styled.p`
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0 0 12px 0;
  height: 100px;
`
